/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, useRef, useState } from "react";
import { Typography, IconButton, TextField, Box } from "@material-ui/core";
import { PenEditIcon } from "../../icons";
import useStyles from "./style";

interface EditableTextFieldProps {
  placeholder: string;
  isEditable: boolean;
  value?: string;
  onAction: (text: string) => void;
  multiline?: boolean;
}

export default function EditableTextField(props: EditableTextFieldProps) {
  const { placeholder, value, onAction, multiline, isEditable } = props;
  const [text, setText] = useState(value?.trim() || "");
  const [isEditMode, setIsEditMode] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const classes = useStyles();

  const handleOnAction = () => {
    onAction(text.trim());
  };

  const onEnableEditClick = () => {
    setIsEditMode(true);
    setTimeout(() => {
      ref?.current?.focus();
    }, 1);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);
  };

  const handleOnKeyPress = (event: { key: string }) => {
    if (event.key === "Enter") {
      setIsEditMode(false);
      if (text !== value) handleOnAction();
    }
  };

  const handleOnBlur = () => {
    setIsEditMode(false);
    if (text !== value) handleOnAction();
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      className={classes.root}
    >
      {!isEditMode && isEditable ? (
        <Box
          className={classes.editableTextfield}
          onClick={() => onEnableEditClick()}
        >
          <Typography
            variant="body1"
            style={{ lineHeight: 2 }}
            data-testid="comment-content-typo"
          >
            {text || placeholder}
          </Typography>
          <IconButton
            onClick={() => onEnableEditClick()}
            size="small"
            className={classes.editIcon}
            aria-label="edit"
          >
            <PenEditIcon isInteractive />
          </IconButton>
        </Box>
      ) : (
        <TextField
          className={classes.editableTextfield}
          disabled={!isEditMode}
          value={text}
          data-testId="comment-content-input"
          fullWidth
          onChange={onChange}
          InputProps={{ disableUnderline: true }}
          inputRef={ref}
          onKeyPress={handleOnKeyPress}
          onBlur={handleOnBlur}
          multiline={multiline}
        />
      )}
    </Box>
  );
}
